import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import { ACTIV8_TABS } from '../../constants';
import {
  deleteActiv8Content,
  findActiv8Content,
  findActiv8Tags,
  getActiv8ContentUsers,
  getActiv8ProjectName,
  editTags,
  updateContentById,
} from './activ8Actions';

const initialState = {
  projectName: '',
  activ8Tab: ACTIV8_TABS.FIND,
  isAddMessageToStoryMode: false,

  content: [],
  isLoading: false,
  tableSettings: {
    page: 0,
    size: 16,
    totalElements: 0,
    searchQuery: '',
    sort: '-createdOn',
    filters: {
      reaction: [],
      mediaType: [],
      tags: [],
      negativeQuery: [],
      username: [],
    },
  },

  tags: [],
  localTags: [],
  isTagsLoading: false,

  contentUsers: [],
  isContentUsersLoading: false,
};

const activ8Slice = createSlice({
  name: 'activ8',
  initialState,
  reducers: {
    changeActiv8Tab: (state, action) => {
      state.activ8Tab = action.payload;
    },
    setAddMessageToStoryMode: (state, action) => {
      state.isAddMessageToStoryMode = action.payload;
    },
    changeTableSettings: (state, action) => {
      state.tableSettings = { ...state.tableSettings, ...action.payload };
    },
    addTagFilter: (state, action) => {
      const newTag = { value: action.payload, label: action.payload };
      if (!state.tableSettings.filters.tags.some((tag) => tag.value === newTag.value)) {
        state.tableSettings.filters.tags = [...state.tableSettings.filters.tags, newTag];
      }
    },
    localSetContent: (state, action) => {
      state.content = [...action.payload, ...state.content];
    },
    addLocalTag: (state, action) => {
      state.localTags = [...state.localTags, action.payload];
    },
    likeLocalMedia: (state, action) => {
      const { id, reaction } = action.payload;
      state.content = state.content.map((item) => ({
        ...item,
        media: item.media.map((mediaItem) => (mediaItem.id === id ? { ...mediaItem, reaction: reaction } : mediaItem)),
      }));
    },
    resetParams: () => initialState,
  },
  extraReducers: (builder) => {
    builder

      .addCase(getActiv8ProjectName.fulfilled, (state, action) => {
        state.projectName = action.payload;
      })

      .addCase(findActiv8Tags.fulfilled, (state, action) => {
        state.tags = action.payload.content;
        state.isTagsLoading = false;
      })
      .addCase(findActiv8Tags.pending, (state) => {
        state.isTagsLoading = true;
      })
      .addCase(findActiv8Tags.rejected, (state) => {
        state.isTagsLoading = false;
      })

      .addCase(getActiv8ContentUsers.fulfilled, (state, action) => {
        state.contentUsers = action.payload;
        state.isContentUsersLoading = false;
      })
      .addCase(getActiv8ContentUsers.pending, (state) => {
        state.isContentUsersLoading = true;
      })
      .addCase(getActiv8ContentUsers.rejected, (state) => {
        state.isContentUsersLoading = false;
      })

      .addCase(deleteActiv8Content.fulfilled, (state, action) => {
        const { updatedBucket } = action.payload;

        if (!updatedBucket) {
          state.isLoading = false;
          return;
        }

        if (updatedBucket.media.length === 0) {
          state.content = state.content.filter((item) => item.bucketId !== updatedBucket.bucketId);
        } else {
          state.content = state.content.map((item) =>
            item.bucketId === updatedBucket.bucketId ? updatedBucket : item,
          );
        }
        state.isLoading = false;
      })
      .addCase(deleteActiv8Content.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteActiv8Content.rejected, (state) => {
        state.isLoading = false;
      })

      .addMatcher(isAnyOf(findActiv8Content.fulfilled), (state, action) => {
        // INSERT_YOUR_CODE
        // TEMP SOLUTION: Mark media with id 'GodjA58BlL8dYBJDxg-v' as expired for testing
        // state.content = action.payload.content.map((item, index) => ({
        //   bucketId: index + 1,
        //   media: item.map((mediaItem) =>
        //     mediaItem.id === 'Svyo4J4BUhsvXty5og8-' ? { ...mediaItem, status: 'EXPIRED' } : mediaItem,
        //   ),
        // }));
        state.content = action.payload.content.map((item, index) => ({ bucketId: index + 1, media: [...item] }));
        state.tableSettings.totalElements = action.payload.totalElements;
        state.isLoading = false;
      })
      .addMatcher(isAnyOf(findActiv8Content.pending), (state) => {
        state.isLoading = true;
      })
      .addMatcher(isAnyOf(findActiv8Content.rejected), (state) => {
        state.isLoading = false;
      })

      //UPDATE CONTENT BY ID
      .addMatcher(isAnyOf(updateContentById.fulfilled), (state, action) => {
        const updatedContent = action.payload;
        const affectedBucket = state.content.find((item) =>
          item.media.some((mediaItem) => mediaItem.id === updatedContent.id),
        );

        if (affectedBucket) {
          const changedBucket = {
            ...affectedBucket,
            media: affectedBucket.media.map((mediaItem) =>
              mediaItem.id === updatedContent.id ? { ...mediaItem, ...updatedContent } : mediaItem,
            ),
          };
          state.content = state.content.map((item) =>
            item.bucketId === affectedBucket.bucketId ? changedBucket : item,
          );
        }
      })

      //EDIT TAGS
      .addMatcher(isAnyOf(editTags.fulfilled), (state, action) => {
        const { contentId, tags } = action.payload;
        state.content = state.content.map((item) => {
          return {
            ...item,
            media: item.media.map((mediaItem) => (mediaItem.id === contentId ? { ...mediaItem, tags } : mediaItem)),
          };
        });
      });
  },
});

export const {
  resetParams,
  changeTableSettings,
  localSetContent,
  changeActiv8Tab,
  setAddMessageToStoryMode,
  addLocalTag,
  addTagFilter,
  likeLocalMedia,
} = activ8Slice.actions;

export default activ8Slice.reducer;
